import { useEffect, useState } from 'react';
import { testIds } from '../../lib/testIds';

export interface ProgressStepProps {
  status: 'queued' | 'running' | 'complete' | 'failed' | null;
  stepsCompleted: number;
  stepsTotal: number;
  lastStepLabel: string;
  errorMessage?: string | null;
  portalSlug: string;
  /** ISO timestamp the job row was inserted; drives elapsed display. */
  jobCreatedAt?: string | null;
  /** Canonical SharePoint site web URL once provisioning completes.
   *  Comes from `tenantBranding.sharePointSiteUrl`. */
  sharePointSiteUrl?: string | null;
  /** Optional Graph createLink result — one-click "view library" URL.
   *  Comes from `tenantBranding.sharePointShareUrl`. */
  sharePointShareUrl?: string | null;
  onEnterPortal: () => void;
}

/**
 * The 4 backend step labels reported by
 * `packages/repancy-azure/src/portalProvisioner/handler.ts`. Order is
 * load-bearing: stepsCompleted=0 → none done, =1 → site created, etc.
 *
 * `title` + `detail` + `estSec` are user-facing. `backendLabels[]` is
 * the set of substrings that, when seen in `lastStepLabel`, mean this
 * step is currently running. We match on substring (not equality) so a
 * future relabel like `"creating SharePoint site (poll 2/8)"` still
 * lights up the right row.
 */
const STEP_FLOW: ReadonlyArray<{
  title: string;
  detail: string;
  estSec: number;
  backendLabels: ReadonlyArray<string>;
}> = [
  {
    title: 'Creating SharePoint site',
    detail:
      'Spinning up a fresh Microsoft 365 group + Team site for your firm.',
    estSec: 20,
    backendLabels: ['creating SharePoint site', 'creating-site'],
  },
  {
    title: 'Setting up your folder layout',
    detail:
      'Uploading the 00-Context profile + lock file your portal reads on first load.',
    estSec: 8,
    backendLabels: ['seeding folder skeleton', 'folder-skeleton'],
  },
  {
    title: 'Adding sample matters',
    detail:
      'Creating per-matter folders (Inputs / Drafts / Final / Audit / Reviewer-Notes) and uploading the starter documents.',
    estSec: 15,
    backendLabels: ['creating matter folders', 'matter-folders'],
  },
  {
    title: 'Wiring your portal',
    detail:
      'Linking the SharePoint site to your Repancy account so /c/your-slug opens it.',
    estSec: 4,
    backendLabels: ['wiring', 'complete', 'done'],
  },
];

const TOTAL_EST_SEC = STEP_FLOW.reduce((s, x) => s + x.estSec, 0);

function elapsedSeconds(createdAt: string | null | undefined, now: number): number {
  if (!createdAt) return 0;
  const t = Date.parse(createdAt);
  if (!Number.isFinite(t)) return 0;
  return Math.max(0, Math.round((now - t) / 1000));
}

function formatSeconds(s: number): string {
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}m ${r.toString().padStart(2, '0')}s`;
}

/**
 * Returns the index (0..n-1) of the step the wizard *thinks* is
 * currently running, given the backend's `stepsCompleted` count and
 * `lastStepLabel`. We trust `stepsCompleted` first; `lastStepLabel`
 * is a tiebreaker for the queued→step-1 transition (where
 * stepsCompleted is still 0 but the label has already advanced).
 */
function currentRunningIndex(
  stepsCompleted: number,
  label: string,
): number {
  if (stepsCompleted >= STEP_FLOW.length) return STEP_FLOW.length - 1;
  const normalized = (label ?? '').toLowerCase();
  for (let i = 0; i < STEP_FLOW.length; i++) {
    if (STEP_FLOW[i]!.backendLabels.some((l) => normalized.includes(l))) {
      return i;
    }
  }
  return stepsCompleted;
}

function Icon({ kind }: { kind: 'done' | 'running' | 'pending' | 'failed' | 'queued' }) {
  if (kind === 'done') {
    return (
      <span
        aria-hidden="true"
        className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-[#2e7d32] bg-[#c8e6c9] text-xs font-bold text-[#1b5e20]"
      >
        ✓
      </span>
    );
  }
  if (kind === 'running') {
    return (
      <span
        aria-hidden="true"
        className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-[#316ac5] bg-white"
      >
        <span className="h-3 w-3 animate-spin rounded-full border-2 border-[#316ac5] border-t-transparent" />
      </span>
    );
  }
  if (kind === 'failed') {
    return (
      <span
        aria-hidden="true"
        className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-[#a00] bg-[#fee] text-xs font-bold text-[#a00]"
      >
        ×
      </span>
    );
  }
  if (kind === 'queued') {
    return (
      <span
        aria-hidden="true"
        className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-[#888] bg-[#f4e08a] text-xs font-bold text-[#5a4a00]"
      >
        ⋯
      </span>
    );
  }
  return (
    <span
      aria-hidden="true"
      className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-[#888] bg-white text-xs text-[#888]"
    >
      ○
    </span>
  );
}

export function ProgressStep({
  status,
  stepsCompleted,
  stepsTotal,
  lastStepLabel,
  errorMessage,
  portalSlug,
  jobCreatedAt,
  sharePointSiteUrl,
  sharePointShareUrl,
  onEnterPortal,
}: ProgressStepProps) {
  const isComplete = status === 'complete';
  const isFailed = status === 'failed';
  const isQueued = status === 'queued';

  // Tick once per second while the job is in flight so the elapsed
  // clock + "queued for too long" banner stay live without a reload.
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    if (isComplete || isFailed) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [isComplete, isFailed]);

  const elapsed = elapsedSeconds(jobCreatedAt, now);
  const runningIdx = currentRunningIndex(stepsCompleted, lastStepLabel);
  const percent =
    stepsTotal > 0 ? Math.round((stepsCompleted / stepsTotal) * 100) : 0;
  const estRemaining = Math.max(0, TOTAL_EST_SEC - elapsed);
  // "Stuck queued" heuristic: the dispatcher should pick the row up
  // within a few seconds. If we're still on label="queued" + status="queued"
  // after 45s, something went wrong silently and we should say so.
  const isStuckQueued =
    isQueued && elapsed > 45 && lastStepLabel.toLowerCase() === 'queued';

  return (
    <section
      data-testid={testIds.onboard.progressStep}
      className="flex flex-col gap-4 p-4"
    >
      <header className="flex flex-col gap-1">
        <h2 className="text-lg font-bold text-black">
          {isComplete
            ? 'Done — your portal is ready.'
            : isFailed
              ? 'Provisioning failed.'
              : 'Creating your portal…'}
        </h2>
        {!isComplete && !isFailed && (
          <p className="text-xs text-[#333]">
            Elapsed <strong>{formatSeconds(elapsed)}</strong>
            {estRemaining > 0 && !isStuckQueued ? (
              <>
                {' '}· About <strong>{formatSeconds(estRemaining)}</strong> remaining
              </>
            ) : null}
            {' '}· /c/{portalSlug || '…'}
          </p>
        )}
      </header>

      {!isComplete && !isFailed && (
        <>
          <div
            data-testid="onboard-progress-bar-outer"
            className="h-3 w-full overflow-hidden rounded-sm border border-[#404040] bg-[#d0d0d0]"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={percent}
          >
            <div
              data-testid="onboard-progress-bar-inner"
              className="h-full bg-[#316ac5] transition-all duration-500"
              style={{ width: `${percent}%` }}
            />
          </div>

          <ol
            data-testid="onboard-progress-timeline"
            className="flex flex-col gap-2 border border-[#bbb] bg-white p-3"
          >
            {STEP_FLOW.map((step, idx) => {
              const isDone = idx < stepsCompleted;
              const isRunning = !isDone && idx === runningIdx && !isQueued;
              const isCurrentQueued = !isDone && idx === 0 && isQueued;
              const iconKind: Parameters<typeof Icon>[0]['kind'] = isDone
                ? 'done'
                : isCurrentQueued
                  ? 'queued'
                  : isRunning
                    ? 'running'
                    : 'pending';
              return (
                <li
                  key={step.title}
                  data-testid={`onboard-progress-step-${idx}`}
                  data-state={iconKind}
                  className={`flex items-start gap-3 ${
                    isRunning || isCurrentQueued ? 'opacity-100' : isDone ? 'opacity-90' : 'opacity-60'
                  }`}
                >
                  <Icon kind={iconKind} />
                  <div className="flex flex-col">
                    <span
                      className={`text-sm ${
                        isRunning || isCurrentQueued
                          ? 'font-bold text-black'
                          : 'text-black'
                      }`}
                    >
                      {step.title}
                      {isRunning ? (
                        <span className="ml-1 text-xs font-normal text-[#316ac5]">
                          in progress…
                        </span>
                      ) : null}
                      {isCurrentQueued ? (
                        <span className="ml-1 text-xs font-normal text-[#7a5d00]">
                          waiting for worker…
                        </span>
                      ) : null}
                      {isDone ? (
                        <span className="ml-1 text-xs font-normal text-[#2e7d32]">
                          done
                        </span>
                      ) : null}
                    </span>
                    <span className="text-xs text-[#333]">{step.detail}</span>
                  </div>
                </li>
              );
            })}
          </ol>

          {isStuckQueued && (
            <p
              data-testid="onboard-progress-stuck-queued"
              className="border border-[#a07000] bg-[#fff4d0] p-3 text-xs text-black"
            >
              <strong>Heads up:</strong> the background worker hasn't picked
              up this job after {formatSeconds(elapsed)}. This is unusual —
              the dispatcher normally starts within a few seconds. If this
              persists, contact ops with slug{' '}
              <code className="bg-white px-1">{portalSlug}</code>.
            </p>
          )}
        </>
      )}

      {isFailed && errorMessage && (
        <p
          data-testid="onboard-progress-error"
          className="border border-[#a00] bg-[#fee] p-3 text-sm text-black"
        >
          {errorMessage}
        </p>
      )}

      {isComplete && (
        <div className="flex flex-col gap-3">
          {/* SharePoint deep-links — the actual proof the portal exists
              outside Repancy. sharePointSiteUrl is the canonical site
              home; sharePointShareUrl is the optional Graph createLink
              that bypasses the SP login screen if the user is already
              signed into the tenant. */}
          {(sharePointSiteUrl || sharePointShareUrl) && (
            <section
              data-testid="onboard-progress-sharepoint-links"
              className="flex flex-col gap-2 border border-[#bbb] bg-white p-3 text-sm text-black"
            >
              <p className="font-bold">Your SharePoint site is live:</p>
              {sharePointSiteUrl && (
                <a
                  href={sharePointSiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="onboard-progress-sharepoint-site"
                  className="break-all text-[#0a246a] underline hover:text-[#003c74]"
                >
                  {sharePointSiteUrl} ↗
                </a>
              )}
              {sharePointShareUrl && (
                <a
                  href={sharePointShareUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="onboard-progress-sharepoint-share"
                  className="break-all text-[#0a246a] underline hover:text-[#003c74]"
                >
                  Open document library directly ↗
                </a>
              )}
            </section>
          )}
          <button
            type="button"
            data-testid="onboard-enter-portal"
            onClick={onEnterPortal}
            className="self-start border border-[#404040] bg-[#d0d0d0] px-4 py-1 text-sm font-bold text-black cursor-pointer hover:bg-[#e0e0e0]"
          >
            Enter /c/{portalSlug}
          </button>
        </div>
      )}
    </section>
  );
}
